import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import { metricsApi, MetricCreate, MetricUpdate, MetricList as MetricListType } from '../api/metricsApi';
import { assessmentPeriodMetricsApi, AssessmentPeriodMetricCreate, AssessmentPeriodMetricUpdate, AssessmentPeriodMetricList } from '../api/assessmentPeriodMetricsApi';
import { MetricTypeSelector, MetricType, validateMetricValue } from './MetricTypeSelector';
import { assessmentPeriodApi } from 'areas/assessment-schedule/api/assessmentPeriodApi';
import { useGetAllTeamsQuery } from 'areas/team-management/api/teamApi';
import { useApiAdminV1OrganizationListQuery } from 'store/generated/admin/organizations';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from 'common/selectors/auth';

type TabType = 'definitions' | 'values';

interface MetricFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  metricToEdit?: MetricListType | AssessmentPeriodMetricList | null;
  tabType: TabType;
}

export const MetricForm: React.FC<MetricFormProps> = ({
  open,
  onClose,
  onSuccess,
  metricToEdit,
  tabType,
}) => {
  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  
  // Fields for Metric definition
  const [metricType, setMetricType] = useState<number>(MetricType.NPS);
  const [metricName, setMetricName] = useState('');
  const [metricLabel, setMetricLabel] = useState('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [description, setDescription] = useState('');
  
  // Fields for AssessmentPeriodMetric value
  const [metricId, setMetricId] = useState<number | null>(null);
  const [assessmentPeriodId, setAssessmentPeriodId] = useState<number | null>(null);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [teamId, setTeamId] = useState<number | null | undefined>(null);
  const [value, setValue] = useState<number>(0);
  const [targetValue, setTargetValue] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [collectedDate, setCollectedDate] = useState('');
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: assessmentPeriods = [] } = assessmentPeriodApi.useApiPortalV1AssessmentsListQuery();
  const { data: availableMetrics = [] } = metricsApi.useGetMetricsQuery({
    is_active: true,
  });
  const { data: organizations = [] } = useApiAdminV1OrganizationListQuery();
  const { data: teamsResponse } = useGetAllTeamsQuery();
  
  // Filter teams by selected organization
  const teams = useMemo(() => {
    if (!teamsResponse || !organizationId) return [];
    if (Array.isArray(teamsResponse)) {
      return teamsResponse.filter((team: any) => team.organization === organizationId);
    }
    if (teamsResponse && typeof teamsResponse === 'object' && 'results' in teamsResponse) {
      const results = Array.isArray((teamsResponse as any).results) ? (teamsResponse as any).results : [];
      return results.filter((team: any) => team.organization === organizationId);
    }
    if (teamsResponse && typeof teamsResponse === 'object' && 'data' in teamsResponse) {
      const data = Array.isArray((teamsResponse as any).data) ? (teamsResponse as any).data : [];
      return data.filter((team: any) => team.organization === organizationId);
    }
    return [];
  }, [teamsResponse, organizationId]);
  
  const [createMetric, { isLoading: isCreatingMetric }] = metricsApi.useCreateMetricMutation();
  const [updateMetric, { isLoading: isUpdatingMetric }] = metricsApi.usePartialUpdateMetricMutation();
  const [createPeriodMetric, { isLoading: isCreatingPeriodMetric }] = assessmentPeriodMetricsApi.useCreateAssessmentPeriodMetricMutation();
  const [updatePeriodMetric, { isLoading: isUpdatingPeriodMetric }] = assessmentPeriodMetricsApi.usePartialUpdateAssessmentPeriodMetricMutation();
  
  const isCreating = isCreatingMetric || isCreatingPeriodMetric;
  const isUpdating = isUpdatingMetric || isUpdatingPeriodMetric;

  useEffect(() => {
    if (metricToEdit) {
      if (tabType === 'definitions') {
        const defMetric = metricToEdit as MetricListType;
        setMetricType(defMetric.metric_type);
        setMetricName(defMetric.metric_name);
        setMetricLabel(defMetric.metric_label || '');
        setIsActive(defMetric.is_active);
        setDescription(''); // Description not in list type, would need to fetch full metric
      } else {
        const periodMetric = metricToEdit as AssessmentPeriodMetricList;
        setMetricId(periodMetric.metric);
        setAssessmentPeriodId(periodMetric.assessment_period);
        setOrganizationId(periodMetric.organization || null);
        setTeamId(periodMetric.team);
        setValue(periodMetric.value);
        setTargetValue(periodMetric.target_value);
        setNotes(''); // Notes not in list type
        setDataSource(periodMetric.data_source || '');
        setCollectedDate(periodMetric.collected_date ? periodMetric.collected_date.split('T')[0] : '');
      }
    } else {
      // Reset form based on tab type
      if (tabType === 'definitions') {
        setMetricType(MetricType.NPS);
        setMetricName('');
        setMetricLabel('');
        setIsActive(true);
        setDescription('');
      } else {
        setMetricId(null);
        setAssessmentPeriodId(null);
        setOrganizationId(currentOrganizationId || null);
        setTeamId(null);
        setValue(0);
        setTargetValue(null);
        setNotes('');
        setDataSource('');
        setCollectedDate('');
      }
    }
    setErrors({});
  }, [metricToEdit, open, tabType, currentOrganizationId]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (tabType === 'definitions') {
      if (!metricName.trim()) {
        newErrors.metric_name = 'Metric name is required';
      }
    } else {
      if (!metricId) {
        newErrors.metric = 'Metric definition is required';
      }
      
      if (!assessmentPeriodId) {
        newErrors.assessment_period = 'Assessment period is required';
      }
      
      if (!organizationId) {
        newErrors.organization = 'Organization is required';
      }
      
      // Get metric type from selected metric for validation
      const selectedMetric = availableMetrics.find(m => m.id === metricId);
      if (selectedMetric) {
        const valueError = validateMetricValue(selectedMetric.metric_type, value);
        if (valueError) {
          newErrors.value = valueError;
        }
        
        if (targetValue !== null && targetValue !== undefined) {
          const targetError = validateMetricValue(selectedMetric.metric_type, targetValue);
          if (targetError) {
            newErrors.target_value = targetError;
          }
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      if (tabType === 'definitions') {
        const payload: MetricCreate | MetricUpdate = {
          metric_type: metricType,
          metric_name: metricName.trim(),
          metric_label: metricLabel.trim() || null,
          is_active: isActive,
          description: description.trim() || null,
        };

        if (metricToEdit?.id) {
          await updateMetric({ id: metricToEdit.id, data: payload }).unwrap();
        } else {
          await createMetric(payload as MetricCreate).unwrap();
        }
      } else {
        if (!metricId || !assessmentPeriodId || !organizationId) return;
        
        const payload: AssessmentPeriodMetricCreate | AssessmentPeriodMetricUpdate = {
          metric: metricId,
          assessment_period: assessmentPeriodId,
          organization: organizationId,
          team: teamId === null ? undefined : teamId,
          value,
          target_value: targetValue !== null && targetValue !== undefined ? targetValue : null,
          data_source: dataSource.trim() || null,
          collected_date: collectedDate || null,
          notes: notes.trim() || null,
        };

        if (metricToEdit?.id) {
          await updatePeriodMetric({ id: metricToEdit.id, data: payload }).unwrap();
        } else {
          await createPeriodMetric(payload as AssessmentPeriodMetricCreate).unwrap();
        }
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Save failed:', err);
      if (err?.data) {
        const apiErrors: Record<string, string> = {};
        if (typeof err.data === 'object') {
          Object.keys(err.data).forEach(key => {
            const errorValue = err.data[key];
            if (Array.isArray(errorValue)) {
              apiErrors[key] = errorValue[0];
            } else if (typeof errorValue === 'string') {
              apiErrors[key] = errorValue;
            }
          });
        }
        setErrors(apiErrors);
      }
    }
  };

  const assessmentPeriodOptions = useMemo(() => 
    [...assessmentPeriods]
      .sort((a, b) => {
        const dateA = a.start_date_time ? new Date(a.start_date_time).getTime() : 0;
        const dateB = b.start_date_time ? new Date(b.start_date_time).getTime() : 0;
        return dateB - dateA;
      })
      .map(period => ({
        key: period.id,
        text: period.name || `Period ${period.id}`,
        value: period.id,
      })),
    [assessmentPeriods]
  );


  const metricOptions = useMemo(() => 
    availableMetrics.map(metric => ({
      key: metric.id,
      text: `${metric.metric_name}${metric.metric_label ? ` (${metric.metric_label})` : ''}`,
      value: metric.id,
    })),
    [availableMetrics]
  );

  const selectedMetric = useMemo(() => 
    tabType === 'values' ? availableMetrics.find(m => m.id === metricId) : null,
    [availableMetrics, metricId, tabType]
  );

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        {metricToEdit 
          ? `Edit ${tabType === 'definitions' ? 'Metric Definition' : 'Period Metric Value'}` 
          : `Create ${tabType === 'definitions' ? 'Metric Definition' : 'Period Metric Value'}`}
      </Modal.Header>
      <Modal.Content>
        <Form>
          {tabType === 'definitions' ? (
            <>
              <Form.Field error={!!errors.metric_type} required>
                <label>Metric Type</label>
                <MetricTypeSelector
                  value={metricType}
                  onChange={setMetricType}
                  error={!!errors.metric_type}
                />
                {errors.metric_type && <Message negative size="tiny">{errors.metric_type}</Message>}
              </Form.Field>

              <Form.Field error={!!errors.metric_name} required>
                <label>Metric Name</label>
                <Form.Input
                  value={metricName}
                  onChange={(e, { value }) => setMetricName(value)}
                  placeholder="e.g., Q3 NPS, Customer Satisfaction"
                />
                {errors.metric_name && <Message negative size="tiny">{errors.metric_name}</Message>}
              </Form.Field>

              <Form.Field>
                <label>Display Label (Optional)</label>
                <Form.Input
                  value={metricLabel}
                  onChange={(e, { value }) => setMetricLabel(value)}
                  placeholder="e.g., NPS, Revenue"
                />
                <Message info size="tiny">
                  Short label for display in charts. If blank, metric name will be used.
                </Message>
              </Form.Field>

              <Form.Field>
                <label>Active</label>
                <Form.Checkbox
                  checked={isActive}
                  onChange={(e, { checked }) => setIsActive(checked === true)}
                  label="Metric is active and available for use"
                />
              </Form.Field>

              <Form.Field>
                <label>Description (Optional)</label>
                <Form.TextArea
                  value={description}
                  onChange={(e, { value }) => setDescription(value as string)}
                  placeholder="Description of this metric definition"
                  rows={3}
                />
              </Form.Field>
            </>
          ) : (
            <>
              <Form.Field error={!!errors.metric} required>
                <label>Metric Definition</label>
                <Form.Select
                  search
                  options={metricOptions}
                  value={metricId || undefined}
                  onChange={(e, { value }) => setMetricId(value as number)}
                  placeholder="Select metric definition"
                  disabled={!!metricToEdit}
                />
                {errors.metric && <Message negative size="tiny">{errors.metric}</Message>}
                <Message info size="tiny">
                  Select the metric definition this value belongs to. Create metric definitions in the "Metric Definitions" tab.
                </Message>
              </Form.Field>

              <Form.Field error={!!errors.assessment_period} required>
                <label>Assessment Period</label>
                <Form.Select
                  options={assessmentPeriodOptions}
                  value={assessmentPeriodId || undefined}
                  onChange={(e, { value }) => {
                    setAssessmentPeriodId(value as number);
                    // Auto-set organization from assessment period if available
                    const selectedPeriod = assessmentPeriods.find(p => p.id === value);
                    if (selectedPeriod && selectedPeriod.organization) {
                      setOrganizationId(selectedPeriod.organization);
                    }
                  }}
                  placeholder="Select assessment period"
                />
                {errors.assessment_period && <Message negative size="tiny">{errors.assessment_period}</Message>}
              </Form.Field>

              <Form.Field error={!!errors.organization} required>
                <label>Organization</label>
                <Form.Select
                  search
                  options={organizations.map((org: any) => ({
                    key: org.id,
                    text: org.name,
                    value: org.id,
                  }))}
                  value={organizationId || undefined}
                  onChange={(e, { value }) => {
                    setOrganizationId(value as number);
                    setTeamId(null); // Reset team when organization changes
                  }}
                  placeholder="Select organization"
                />
                {errors.organization && <Message negative size="tiny">{errors.organization}</Message>}
                <Message info size="tiny">
                  Organization will be auto-set from assessment period if available.
                </Message>
              </Form.Field>

              <Form.Field error={!!errors.team}>
                <label>Team (Optional)</label>
                <Form.Select
                  options={[
                    { key: 'org', text: 'Organization (No Team)', value: null },
                    ...teams.map((team: any) => ({
                      key: team.id,
                      text: team.name,
                      value: team.id,
                    })),
                  ]}
                  value={teamId === null ? 'org' : (teamId || undefined)}
                  onChange={(e, { value }) => setTeamId(value === 'org' ? null : (value as number | null))}
                  placeholder="Select team (leave blank for organization-level)"
                  disabled={!organizationId}
                />
                {errors.team && <Message negative size="tiny">{errors.team}</Message>}
                <Message info size="tiny">
                  Leave blank for organization-level metric, or select a team for team-specific metric.
                </Message>
              </Form.Field>

              <Form.Field error={!!errors.value} required>
                <label>Value</label>
                <Form.Input
                  type="number"
                  step="0.01"
                  value={value}
                  onChange={(e, { value }) => setValue(parseFloat(value) || 0)}
                  placeholder="Enter metric value"
                />
                {errors.value && <Message negative size="tiny">{errors.value}</Message>}
                {selectedMetric && selectedMetric.metric_type === MetricType.NPS && (
                  <Message info size="tiny">
                    NPS scores range from -100 to 100.
                  </Message>
                )}
              </Form.Field>

              <Form.Field error={!!errors.target_value}>
                <label>Target Value (Optional)</label>
                <Form.Input
                  type="number"
                  step="0.01"
                  value={targetValue !== null ? targetValue : ''}
                  onChange={(e, { value }) => setTargetValue(value ? parseFloat(value) : null)}
                  placeholder="Enter target value (overrides metric default)"
                />
                {errors.target_value && <Message negative size="tiny">{errors.target_value}</Message>}
              </Form.Field>

              <Form.Field>
                <label>Data Source (Optional)</label>
                <Form.Input
                  value={dataSource}
                  onChange={(e, { value }) => setDataSource(value)}
                  placeholder="e.g., Platform, Manual Entry, Survey"
                />
              </Form.Field>

              <Form.Field>
                <label>Collected Date (Optional)</label>
                <Form.Input
                  type="date"
                  value={collectedDate}
                  onChange={(e, { value }) => setCollectedDate(value)}
                />
              </Form.Field>

              <Form.Field>
                <label>Notes (Optional)</label>
                <Form.TextArea
                  value={notes}
                  onChange={(e, { value }) => setNotes(value as string)}
                  placeholder="Additional notes about this metric value"
                  rows={3}
                />
              </Form.Field>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isCreating || isUpdating}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSave}
          loading={isCreating || isUpdating}
          disabled={
            tabType === 'definitions' 
              ? !metricName.trim()
              : (!metricId || !assessmentPeriodId || !organizationId)
          }
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

